import { useState, useEffect } from "react";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const API = "/api";

export default function PropertyFilters({ onFilterChange, filters }) {
  const [cities, setCities] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [citiesRes, typesRes] = await Promise.all([
          axios.get(`${API}/properties/cities`),
          axios.get(`${API}/properties/types`)
        ]);
        setCities(citiesRes.data);
        setPropertyTypes(typesRes.data);
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
  };

  const applyFilters = () => {
    onFilterChange(localFilters);
    setShowMobileFilters(false);
  };

  const resetFilters = () => {
    const emptyFilters = {
      price_min: undefined,
      price_max: undefined,
      surface_min: undefined,
      surface_max: undefined,
      city: undefined,
      property_type: undefined,
      rooms_min: undefined
    };
    setLocalFilters(emptyFilters);
    onFilterChange(emptyFilters);
    setShowMobileFilters(false);
  };

  const FilterContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
      {/* City */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Ville</label>
        <Select 
          value={localFilters.city || "all"} 
          onValueChange={(v) => handleChange('city', v === "all" ? undefined : v)}
        >
          <SelectTrigger className="rounded-none border-zinc-200" data-testid="filter-city">
            <SelectValue placeholder="Toutes les villes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les villes</SelectItem>
            {cities.map((city) => (
              <SelectItem key={city} value={city}>{city}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Property Type */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Type</label>
        <Select 
          value={localFilters.property_type || "all"} 
          onValueChange={(v) => handleChange('property_type', v === "all" ? undefined : v)}
        >
          <SelectTrigger className="rounded-none border-zinc-200" data-testid="filter-type">
            <SelectValue placeholder="Tous les types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {propertyTypes.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Min */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Prix min</label>
        <Input
          type="number"
          placeholder="0 €"
          value={localFilters.price_min || ""}
          onChange={(e) => handleChange('price_min', e.target.value ? parseInt(e.target.value) : undefined)}
          className="rounded-none border-zinc-200"
          data-testid="filter-price-min"
        />
      </div>

      {/* Price Max */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Prix max</label>
        <Input
          type="number"
          placeholder="Illimité"
          value={localFilters.price_max || ""}
          onChange={(e) => handleChange('price_max', e.target.value ? parseInt(e.target.value) : undefined)}
          className="rounded-none border-zinc-200"
          data-testid="filter-price-max"
        />
      </div>

      {/* Surface Min */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Surface min</label>
        <Input
          type="number"
          placeholder="0 m²"
          value={localFilters.surface_min || ""}
          onChange={(e) => handleChange('surface_min', e.target.value ? parseInt(e.target.value) : undefined)}
          className="rounded-none border-zinc-200"
          data-testid="filter-surface-min"
        />
      </div>

      {/* Rooms Min */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Pièces min</label>
        <Select 
          value={localFilters.rooms_min?.toString() || "all"} 
          onValueChange={(v) => handleChange('rooms_min', v === "all" ? undefined : parseInt(v))}
        >
          <SelectTrigger className="rounded-none border-zinc-200" data-testid="filter-rooms">
            <SelectValue placeholder="Toutes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
            <SelectItem value="5">5+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Actions */}
      <div className="flex items-end gap-2">
        <Button
          onClick={applyFilters}
          className="flex-1 bg-[#D62965] hover:bg-[#A61E4E] text-white rounded-none"
          data-testid="filter-apply-btn"
        >
          <Search className="h-4 w-4 mr-2" />
          Rechercher
        </Button>
        <Button
          onClick={resetFilters}
          variant="outline"
          className="rounded-none border-zinc-200"
          data-testid="filter-reset-btn"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Filters */}
      <div className="hidden lg:block bg-white border border-zinc-200 p-6" data-testid="filters-desktop">
        <FilterContent />
      </div>

      {/* Mobile Filter Toggle */}
      <div className="lg:hidden">
        <Button
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          variant="outline"
          className="w-full rounded-none border-zinc-200 justify-between"
          data-testid="filter-mobile-toggle"
        >
          <span className="flex items-center">
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filtres de recherche
          </span>
          {Object.values(localFilters).filter(Boolean).length > 0 && (
            <span className="bg-[#D62965] text-white text-xs px-2 py-0.5 rounded-full">
              {Object.values(localFilters).filter(Boolean).length}
            </span>
          )}
        </Button>

        {/* Mobile Filters Panel */}
        {showMobileFilters && (
          <div className="mt-4 bg-white border border-zinc-200 p-4" data-testid="filters-mobile">
            <FilterContent />
          </div>
        )}
      </div>
    </>
  );
}
